import Request from "../Request";
import {
    CrossClientDashboardData,
    ClientDashboardData,
    AchievementFilter,
    PeriodType,
    ComparisonType,
    MetricSet,
    MetricComparison
} from "../../interfaces/dashboards/CrossClientDashboard.interface";

class CrossClientDashboardService {
    /**
     * Fetch dashboard data from API
     */
    static async getDashboardData(): Promise<CrossClientDashboardData> {
        try {
            const response = await Request.get('/api/cross-client-dashboard');
            return response;
        } catch (error) {
            console.error('Failed to fetch cross-client dashboard data:', error);
            throw error;
        }
    }

    /**
     * Filter clients by achievement status
     */
    static filterClientsByAchievement(
        clients: ClientDashboardData[],
        filter: AchievementFilter,
        period: PeriodType,
        comparison: ComparisonType
    ): ClientDashboardData[] {
        if (filter === 'all') return clients;

        return clients.filter(client => {
            const metric = this.getSelectedMetrics(client, period, comparison);
            const salesCompPercent = this.calculateComparisonPercent(metric.sales);

            if (filter === 'over') {
                return salesCompPercent > 0; // Actual > Comparison (over-achieving)
            } else {
                return salesCompPercent < 0; // Actual < Comparison (under-achieving)
            }
        });
    }

    /**
     * Filter clients by selected client IDs
     */
    //todo: we should add filters for clients with unmapped, or no plans
    static filterClientsBySelection(
        clients: ClientDashboardData[],
        selectedClientIds: number[]
    ): ClientDashboardData[] {
        if (!selectedClientIds || selectedClientIds.length === 0) {
            return clients;
        }
        return clients.filter(client => selectedClientIds.includes(client.clientId));
    }

    /**
     * Sort clients by client code using natural/alphanumeric sorting
     * This ensures A1, A2, A10, A100 instead of A1, A10, A100, A2
     */
    static sortClientsByCode(clients: ClientDashboardData[]): ClientDashboardData[] {
        return [...clients].sort((a, b) => {
            // Use localeCompare with numeric option for natural sorting
            return a.clientCode.localeCompare(b.clientCode, undefined, {
                numeric: true,
                sensitivity: 'base'
            });
        });
    }

    /**
     * Get the selected metrics based on period and comparison type
     */
    static getSelectedMetrics(
        client: ClientDashboardData,
        period: PeriodType,
        comparison: ComparisonType
    ): MetricSet {
        const periodKey = period === 'current' ? 'currentMonth' : 'lastMonth';
        const comparisonKey = comparison === 'plan' ? 'vsPlan' : 'vsLastYear';

        return client.metrics[periodKey][comparisonKey];
    }

    /**
     * Derive variance from trended vs comparison values when available so the
     * badge/copy reflect projected MTD performance instead of duplicated actuals.
     */
    static calculateComparisonPercent(metric: MetricComparison): number {
        const trended = Number(metric?.trended);
        const comparison = Number(metric?.comparison);
        const fallback = Number(metric?.comparisonPercent);

        if (Number.isFinite(trended) && Number.isFinite(comparison)) {
            if (comparison === 0) {
                if (trended === 0) {
                    return 0;
                }

                return Number.isFinite(fallback) ? fallback : 0;
            }

            return ((trended - comparison) / Math.abs(comparison)) * 100;
        }

        return Number.isFinite(fallback) ? fallback : 0;
    }

    /**
     * Format number as money
     */
    static moneyFormat(value: number, wholeNumber: boolean = true): string {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: wholeNumber ? 0 : 2,
            maximumFractionDigits: wholeNumber ? 0 : 2
        }).format(value);
    }

    /**
     * Format number as percentage
     */
    static percentageFormat(value: number, decimals: number = 0): string {
        return new Intl.NumberFormat('en-US', {
            style: 'percent',
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals
        }).format(value / 100);
    }

    /**
     * Get comparison label text
     */
    static getComparisonLabel(comparisonType: ComparisonType): string {
        return comparisonType === 'plan' ? 'vs Plan' : 'vs Last Year';
    }

    /**
     * Get period label text
     */
    static getPeriodLabel(periodType: PeriodType): string {
        return periodType === 'current' ? 'Current Month' : 'Last Month';
    }

    /**
     * Format timestamp for display
     */
    static formatTimestamp(timestamp: string | null | any): string {
        if (!timestamp) return 'N/A';

        try {
            let dateString: string;

            // Handle PHP DateTime object format: {date: "2025-12-03 21:45:28.000000", timezone_type: 3, timezone: "America/Los_Angeles"}
            if (typeof timestamp === 'object' && timestamp.date) {
                dateString = timestamp.date.split('.')[0]; // Remove microseconds
            }
            // Handle string format: "2025-12-04 01:09:45"
            else if (typeof timestamp === 'string') {
                dateString = timestamp;
            }
            else {
                console.warn('Unknown timestamp format:', timestamp);
                return 'Invalid Date';
            }

            // Convert MySQL/BigQuery datetime format to ISO 8601
            // Replace space with T to make it ISO 8601 compliant
            const isoTimestamp = dateString.replace(' ', 'T');
            const date = new Date(isoTimestamp);

            // Check if date is valid
            if (isNaN(date.getTime())) {
                console.warn('Invalid timestamp format:', timestamp);
                return 'Invalid Date';
            }

            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });
        } catch (error) {
            console.error('Error formatting timestamp:', timestamp, error);
            return 'Invalid Date';
        }
    }

    /**
     * Get display string for client selection dropdown
     */
    static getClientDisplayString(client: ClientDashboardData): string {
        return `${client.clientCode} - ${client.clientName}`;
    }

    /**
     * Get color class based on comparison percentage
     */
    static getComparisonColor(comparisonPercent: number): string {
        if (comparisonPercent > 0) {
            return 'text-emerald-600'; // Over-achieving (green)
        } else if (comparisonPercent < 0) {
            return 'text-red-600'; // Under-achieving (red)
        } else {
            return 'text-gray-600'; // On target (gray)
        }
    }
}

export default CrossClientDashboardService;

